import {
  Button,
  Checkbox,
  cn,
  DeletableBadge,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogSection,
  DialogTitle,
  Input,
  Label,
  MultiSelect,
  withKey,
} from "@tellescope/ui";
import { Spinner, Select } from "@tellescope/design-system";
import { TemplateEditor } from "@tellescope/templates";
import { ComponentProps, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTemplateFormDialog } from "./useTemplateFormDialog";
import { Plus, X } from "lucide-react";
import { getSMSParts, validateUrl } from "@tellescope/utils";

export interface TemplateFormDialogContentsProps extends ComponentProps<typeof DialogContent> {
  templateId?: string;
  mode?: "create" | "duplicate" | "edit";
  /** Called after the template is saved, so the caller can close the dialog. */
  onSuccess?: () => void;
}

export const TemplateFormDialogContents = withKey(
  ({ templateId, mode, onSuccess, className, ...rest }: TemplateFormDialogContentsProps) => {
    const { t } = useTranslation("templates");
    const [mmsUrlInvalid, setMMSUrlInvalid] = useState(false);
    const [mmsUrlInput, setMMSUrlInput] = useState("");
    const {
      form,
      template,
      contentLoading,
      richTextEnabled,
      editorValue,
      onEditorChange,
      isSaving,
      channelOptions,
      roleOptions,
      entityTypeOptions,
      userTagOptions,
      userTagCreator,
    } = useTemplateFormDialog({
      templateId,
      mode,
      onSuccess,
    });

    const handleAddURL = useCallback(() => {
      const hasError = !!validateUrl(mmsUrlInput, { requireProtocol: false }).error;
      if (hasError) {
        setMMSUrlInvalid(true);
        return;
      }
      form.setFieldValue("mmsAttachmentURLs", [...form.values.mmsAttachmentURLs, mmsUrlInput]);
      setMMSUrlInput("");
    }, [form, mmsUrlInput]);

    const handleDeleteURL = useCallback(
      (url: string) => {
        form.setFieldValue(
          "mmsAttachmentURLs",
          form.values.mmsAttachmentURLs.filter((u) => u !== url)
        );
      },
      [form]
    );

    return (
      <DialogContent
        divided
        data-testid="template-form-dialog"
        className={cn("overflow-hidden sm:max-w-[90vw] sm:w-[1200px]", className)}
        {...rest}
      >
        <DialogTitle className="p-4">
          {mode === "create"
            ? t("Create Template")
            : mode === "duplicate"
            ? t("Duplicate Template: {{templateName}}", { templateName: template?.title ?? "" })
            : t("Edit Template: {{templateName}}", { templateName: template?.title ?? "" })}
        </DialogTitle>
        <DialogSection
          className="overflow-y-auto"
          style={{
            maxHeight: "min(80svh, 800px)",
          }}
        >
          {contentLoading ? (
            <div className="flex flex-col items-center justify-center w-full min-h-[500px] gap-4">
              <Spinner size="md" />
              <p className="text-muted-foreground">{t("Loading...")}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center w-full min-h-[500px] gap-4">
              <div className="space-y-2 w-full">
                <Label required className="w-full" htmlFor="title">
                  {t("Title")}
                </Label>
                <Input id="title" data-testid="template-form-title-input" {...form.getFieldProps("title")} autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-4 w-full">
                <div className="space-y-2">
                  <Label className="w-full" htmlFor="type">
                    {t("Type")}
                  </Label>
                  <Select {...form.getCustomFieldProps("type")}>
                    <Select.Trigger className="w-full">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.Item value="enduser">{t("Patient")}</Select.Item>
                      <Select.Item value="Reply">{t("Reply")}</Select.Item>
                      <Select.Item value="team">{t("Team")}</Select.Item>
                    </Select.Content>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="w-full" htmlFor="forChannels">
                    {t("Channels")}
                  </Label>
                  <MultiSelect
                    options={channelOptions}
                    {...form.getCustomFieldProps("forChannels")}
                    className="min-h-0 w-full py-1"
                    placeholder={t("Select channels...")}
                    badgeListOutside
                  />
                </div>
                <div className="space-y-2">
                  <Label className="w-full" htmlFor="forRoles">
                    {t("Roles")}
                  </Label>
                  <MultiSelect
                    options={roleOptions}
                    {...form.getCustomFieldProps("forRoles")}
                    className="min-h-0 w-full py-1"
                    placeholder={t("Select roles...")}
                    badgeListOutside
                  />
                </div>
                <div className="space-y-2">
                  <Label className="w-full" htmlFor="forEntityTypes">
                    {t("Entity Types")}
                  </Label>
                  <MultiSelect
                    options={entityTypeOptions}
                    {...form.getCustomFieldProps("forEntityTypes")}
                    className="min-h-0 w-full py-1"
                    placeholder={t("Select entity types...")}
                    badgeListOutside
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="w-full" htmlFor="forUserTags">
                    {t("User Tags")}
                  </Label>
                  <div className="grid grid-cols-[auto_1fr] gap-2">
                    <Select {...form.getCustomFieldProps("forUserTags.qualifier")}>
                      <Select.Trigger>
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value="One Of">{t("One of")}</Select.Item>
                        <Select.Item value="Any Of">{t("Any of")}</Select.Item>
                      </Select.Content>
                    </Select>
                    <MultiSelect
                      options={userTagOptions}
                      {...form.getCustomFieldProps("forUserTags.values")}
                      className="min-h-0 w-full py-1"
                      placeholder={t("Select user tags...")}
                      creator={userTagCreator}
                      badgeListOutside
                      badgeListClassName={cn("col-span-2")}
                    />
                  </div>
                </div>
              </div>
              {(!form.values.forChannels.length || form.values.forChannels.includes("Email")) && (
                <div className="space-y-2 w-full">
                  <Label required htmlFor="subject">
                    {t("Email Subject")}
                  </Label>
                  <Input
                    id="subject"
                    data-testid="template-form-subject-input"
                    {...form.getFieldProps("subject")}
                    placeholder={t("Email subject...")}
                  />
                </div>
              )}
              <div className="space-y-2 w-full">
                <Label className="w-full" htmlFor="content" required>
                  {t("Content")}
                </Label>
                <TemplateEditor
                  id="content"
                  className="w-full"
                  richTextEnabled={richTextEnabled}
                  value={editorValue}
                  onChange={onEditorChange}
                  preserveAnchorTags
                />
              </div>
              <span className="flex items-center gap-2 w-full">
                <Checkbox
                  id="isMarketing"
                  checked={form.values.isMarketing}
                  onCheckedChange={(checked) => form.setFieldValue("isMarketing", !!checked)}
                />
                <Label htmlFor="isMarketing" className="cursor-pointer">
                  {t("Is Marketing Message")}
                </Label>
              </span>
              {(!form.values.forChannels.length || form.values.forChannels.includes("SMS")) && (
                <>
                  <div className="space-y-2 w-full">
                    <Label className="flex items-center gap-1">
                      {t("MMS Attachment URLs")}
                      <span className="text-xs text-muted-foreground">{t("(only sent on SMS channel)")}</span>
                    </Label>
                    <span className="flex gap-2 items-start">
                      <div className="grow space-y-1">
                        <Input
                          value={mmsUrlInput}
                          onChange={(e) => {
                            setMMSUrlInvalid(false);
                            setMMSUrlInput(e.target.value);
                          }}
                          className={mmsUrlInvalid ? "border-destructive text-destructive" : undefined}
                          placeholder={t("Enter URL and press Enter...")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAddURL();
                            }
                          }}
                        />
                        {mmsUrlInvalid && <p className="text-destructive text-xs">{t("Invalid URL")}</p>}
                      </div>
                      <Button variant="outline" size="sm-icon" className="h-8 w-8" onClick={handleAddURL}>
                        <Plus />
                      </Button>
                    </span>
                    <span className="flex items-center gap-2">
                      {form.values.mmsAttachmentURLs.map((url) => (
                        <DeletableBadge
                          key={url}
                          onClick={() => handleDeleteURL(url)}
                          className="cursor-pointer font-light"
                        >
                          {url}
                          <X />
                        </DeletableBadge>
                      ))}
                    </span>
                  </div>
                  <div className="space-y-2 w-full">
                    <Label>{t("SMS Parts")}</Label>
                    <span className="flex items-center gap-2 text-muted-foreground text-sm font-light">
                      <p>{t("{{count}} part", { count: getSMSParts(form.values.message || "") })}</p>-
                      <p>{t("{{count}} characters", { count: form.values.message?.length ?? 0 })}</p>
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogSection>
        <DialogFooter className="bg-gray-100">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("Cancel")}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            loading={isSaving}
            disabled={!form.isValid || (!form.isDirty && mode !== "duplicate")}
            onClick={(e) => form.handleSubmit(e)}
            data-testid="template-form-submit"
          >
            {templateId ? t("Save Changes") : t("Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ templateId, mode }) => `${templateId ?? ""}-${mode ?? ""}`
);
